import React, { useState, useEffect } from 'react';
import { Image, ImageProps, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const TOKEN_KEY = '@tinomobile:auth_token';

type AuthenticatedImageProps = Omit<ImageProps, 'source'> & {
  uri: string;
};

/**
 * Image component that automatically adds JWT token to requests for authenticated image URLs
 * On web, uses fetch with Authorization header and converts to base64 data URL
 * On native, React Native Image handles Authorization headers automatically via source.headers
 *
 * Note: For web, the API server must have proper CORS headers configured:
 * - Access-Control-Allow-Origin
 * - Access-Control-Allow-Headers: Authorization
 */
export const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  uri,
  ...props
}) => {
  // Check if URI requires authentication
  const requiresAuth = uri && uri.includes('tino-api.grupa-abs.pl');

  // For authenticated URLs, start with empty string to prevent unauthorized request
  const [imageUri, setImageUri] = useState<string>(requiresAuth ? '' : uri);
  const [headers, setHeaders] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(requiresAuth);

  useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      if (requiresAuth) {
        try {
          const token = await AsyncStorage.getItem(TOKEN_KEY);

          // Don't send request without token for authenticated URLs
          if (!token) {
            console.warn(
              'AuthenticatedImage: No token available, skipping image load',
            );
            if (isMounted) {
              setIsLoading(false);
            }
            return;
          }

          if (Platform.OS === 'web') {
            // On web, fetch image with Authorization header and convert to base64 data URL
            const response = await fetch(uri, {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
              },
              mode: 'cors',
              credentials: 'omit',
            });

            if (response.ok && isMounted) {
              const blob = await response.blob();
              // Convert blob to base64 data URL to avoid ORB issues
              const reader = new FileReader();
              reader.onloadend = () => {
                if (isMounted && reader.result) {
                  setImageUri(reader.result as string);
                  setIsLoading(false);
                }
              };
              reader.readAsDataURL(blob);
            } else if (isMounted) {
              console.warn(
                'AuthenticatedImage: fetch failed with status:',
                response.status,
              );
              setIsLoading(false);
            }
          } else {
            // On native, set headers for Image component
            if (isMounted) {
              setHeaders({
                Authorization: `Bearer ${token}`,
              });
              setImageUri(uri);
              setIsLoading(false);
            }
          }
        } catch (error) {
          console.warn('AuthenticatedImage: Error loading image:', error);
          if (isMounted) {
            setIsLoading(false);
          }
        }
      } else {
        // Not an authenticated URL, use as-is
        if (isMounted) {
          setImageUri(uri);
          setIsLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [uri, requiresAuth]);

  // Don't render Image with empty URI to prevent unauthorized requests
  if (!imageUri || isLoading) {
    return null;
  }

  if (Platform.OS === 'web') {
    return <Image source={{ uri: imageUri }} {...props} />;
  }

  // Native: use headers prop
  return <Image source={{ uri: imageUri, headers }} {...props} />;
};

export default AuthenticatedImage;
